import React, { CSSProperties } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SeekerOnboardingService as OnboardingService } from '../services/onboardingService';
import { Footer } from '@ui/components/Footer';
import { SeekerHeader } from '@ui/components/SeekerHeader';
import { getSupabaseClient } from '../lib/supabase';

const ICON_MASK_STYLE: CSSProperties = {
  backgroundColor: 'rgb(var(--landing-icon-gold))',
  maskImage: 'url(/icon.svg)',
  WebkitMaskImage: 'url(/icon.svg)',
  maskRepeat: 'no-repeat',
  WebkitMaskRepeat: 'no-repeat',
  maskPosition: 'center',
  WebkitMaskPosition: 'center',
  maskSize: 'contain',
  WebkitMaskSize: 'contain',
};

const steps = [
  'Basics About You',
  'Experience Preferences',
  'Safety Check',
  'Personalized Guide Match',
];

export function LandingPage() {
  const navigate = useNavigate();

  const startOnboarding = async () => {
    const supabase = getSupabaseClient();
    await supabase.auth.signOut();
    OnboardingService.clearOnboardingData();
    navigate('/onboarding/seeker_over_18');
  };

  return (
    <div className="flex min-h-screen w-full flex-col bg-landing-bg">
      {/* Mobile header */}
      <header className="flex lg:hidden h-16 shrink-0 items-center justify-center bg-landing-header px-4">
        <img src="/gclogo.webp" alt="Guides Collective" className="h-8 w-auto" />
      </header>

      {/* Desktop header */}
      <div className="hidden lg:block">
        <SeekerHeader showLogin onLoginClick={() => navigate('/login')} />
      </div>

      {/* Main content */}
      <main className="flex flex-1 flex-col px-6 lg:px-0 bg-white">
        <div className="mx-auto w-full max-w-lg lg:max-w-2xl py-10 lg:py-16">
          {/* Welcome heading */}
          <h1 className="font-recoleta text-[32px] lg:text-[56px] font-normal leading-[1.1] text-landing-primary mb-6">
            Welcome to{'\n'}Guides Collective
          </h1>

          {/* Intro copy */}
          <p className="text-[16px] lg:text-[18px] leading-[1.6] text-landing-primary mb-2">
            We'll ask you a few short questions so we can match you with the right guide for your journey.
          </p>
          <p className="text-[16px] lg:text-[18px] leading-[1.6] text-landing-primary mb-10">
            It only takes a few minutes, and your answers are always private.
          </p>

          {/* What to expect card */}
          <div className="rounded-2xl border-2 border-landing-primary p-6 lg:p-8 mb-10">
            <h2 className="font-recoleta text-[18px] lg:text-[18px] font-normal text-landing-primary mb-6">
              What to expect
            </h2>
            <div className="flex flex-col gap-5">
              {steps.map((step) => (
                <div key={step} className="flex items-start gap-3">
                  <span
                    className="inline-block h-5 w-5 flex-shrink-0 mt-1"
                    style={ICON_MASK_STYLE}
                    aria-hidden
                  />
                  <span className="font-recoleta text-[18px] lg:text-[24px] text-landing-primary">
                    {step}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA button */}
          <button
            type="button"
            onClick={startOnboarding}
            className="w-full flex items-center justify-center gap-3 rounded-full bg-landing-primary px-6 py-4 text-[18px] font-medium text-landing-text-light transition-colors duration-200 hover:bg-landing-primary/85 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-landing-primary"
          >
            <span>Get Started</span>
            <span className="text-lg">{'\u2192'}</span>
          </button>

          {/* Sign in link */}
          <p className="mt-5 text-center text-[15px] text-landing-primary">
            Already have an account?{' '}
            <Link to="/login" className="font-bold text-landing-primary underline">
              Sign in
            </Link>
          </p>

          {/* Terms */}
          <p className="mt-4 text-center text-[13px] leading-[1.5] text-landing-primary/70">
            By continuing, you agree to our{' '}
            <Link to="/terms/full" className="underline">
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link to="/privacypolicy" className="underline">
              Privacy Policy
            </Link>
          </p>
        </div>
      </main>

      {/* Footer - always visible */}
      <Footer />
    </div>
  );
}
